import {
  ApiClientError,
  type AccessRequestResponse,
  type AuthBootstrapResponse,
  type MeResponse,
  type MembershipSummaryResponse,
  type MyAccessRequestStatusResponse,
  type Permission,
  type PendingInvitationResponse,
  type PendingPlatformInvitationResponse,
  type SessionUserResponse,
  type SubmitJoinAccessRequest,
  type SubmitNewTenantAccessRequest,
} from '@contract-analyzer/api-client';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  AuthContext,
  type AuthUser,
  type OnboardingIdentity,
} from '@/hooks/auth-context';
import { apiClient } from '@/lib/api';
import {
  publishAuthFailed,
  publishAuthRenewed,
  publishAuthSignedOut,
  subscribeAuthRecovery,
  subscribeAuthRenewed,
  subscribeAuthSignedOut,
} from '@/lib/auth-events';
import { approveSessionChange, SessionChangeDeclinedError } from '@/lib/session-change-guard';
import { acceptedCurrentTerms, CURRENT_TERMS_VERSION } from '@/lib/terms';
import {
  IdentityTokenAcquisitionError,
  type DevPersona,
  acquireIdentityTokenSilent,
  isDevAuthMode,
  startDevSessionBootstrap,
  startInteractiveSessionBootstrap,
} from '@/lib/session-auth';
import {
  SessionExpiredDialog,
  type SessionRenewalDialogState,
} from '@/components/layout/session-expired-dialog';
import { withCleanup } from '@/lib/with-cleanup';

const HAS_VISITED_STORAGE_KEY = 'contract-analyzer:has-visited';
const ONBOARDING_STORAGE_KEY = 'contract-analyzer:onboarding';
const SESSION_RENEWAL_BUFFER_MS = 5 * 60 * 1000;
const SESSION_RENEWAL_MIN_DELAY_MS = 10 * 1000;
// Window after a successful renewal during which we ignore further auth-required
// signals. Stale in-flight 401s often arrive moments after recovery completes;
// re-prompting for them produces a "Continue session" flash loop.
const SESSION_RENEWAL_COOLDOWN_MS = 30 * 1000;
// After an interactive bootstrap the browser normally has the freshly-issued
// session cookie stored by the time the response resolves, but a small number of
// MSAL popup + third-party-cookie configurations have been observed to miss the
// cookie on the very next fetch in the same tab. Retrying the cookie-backed
// /users/me request once after a short delay absorbs that race so the page
// rehydrates instead of forcing the "reopen in a new tab" workaround.
const POST_BOOTSTRAP_COOKIE_RETRY_DELAY_MS = 150;

async function fetchCurrentUserWithCookieRetry(): Promise<MeResponse> {
  try {
    return await apiClient.getCurrentUser();
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 401) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, POST_BOOTSTRAP_COOKIE_RETRY_DELAY_MS);
      });
      return await apiClient.getCurrentUser();
    }
    throw error;
  }
}
const SCROLL_ACTIVITY_THROTTLE_MS = 2 * 1000;

function getSessionTimeRemainingMs(user: AuthUser, nowMs: number): number {
  return user.expires_at * 1000 - nowMs;
}

function sessionCanBeProactivelyRenewed(user: AuthUser, nowMs: number): boolean {
  const timeRemainingMs = getSessionTimeRemainingMs(user, nowMs);
  return timeRemainingMs > 0 && timeRemainingMs <= SESSION_RENEWAL_BUFFER_MS;
}

type StoredOnboardingState = {
  readonly identity: OnboardingIdentity;
  readonly request: AccessRequestResponse | null;
  readonly token: string;
};

function buildAuthUser(sessionUser: SessionUserResponse, currentUser: MeResponse): AuthUser {
  return {
    accepted_terms_at: currentUser.accepted_terms_at ?? null,
    created_at: currentUser.created_at,
    display_name: currentUser.display_name,
    email: currentUser.email,
    expires_at: sessionUser.expires_at,
    is_superadmin: sessionUser.is_superadmin,
    membership_id: sessionUser.membership_id,
    permissions: sessionUser.tenant_permissions,
    status: currentUser.status,
    tenant_display_name: sessionUser.tenant_display_name,
    tenant_id: sessionUser.tenant_id,
    tenant_slug: sessionUser.tenant_slug,
    user_audience: currentUser.user_audience,
    user_id: currentUser.user_id,
  };
}

function hasAcceptedCurrentTerms(user: AuthUser | null): boolean {
  return user !== null && acceptedCurrentTerms(user.accepted_terms_at);
}

function markUserAsVisited(): void {
  localStorage.setItem(HAS_VISITED_STORAGE_KEY, '1');
}

function proactiveSessionRenewalEnabled(): boolean {
  return import.meta.env['VITE_AUTH_PROACTIVE_RENEWAL'] !== 'false';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readOnboardingIdentity(value: unknown): OnboardingIdentity | null {
  if (!isRecord(value)) {
    return null;
  }

  const providerSubject =
    typeof value['provider_subject'] === 'string'
      ? value['provider_subject']
      : typeof value['user_id'] === 'string'
        ? value['user_id']
        : null;
  if (
    typeof value['display_name'] !== 'string' ||
    typeof value['email'] !== 'string' ||
    typeof value['identity_provider_tenant_id'] !== 'string' ||
    providerSubject === null
  ) {
    return null;
  }

  return {
    display_name: value['display_name'],
    email: value['email'],
    identity_provider_tenant_id: value['identity_provider_tenant_id'],
    provider_subject: providerSubject,
  };
}

function isAccessRequestResponse(value: unknown): value is AccessRequestResponse {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value['created_at'] === 'string' &&
    typeof value['request_id'] === 'string' &&
    typeof value['requester_display_name'] === 'string' &&
    typeof value['requester_email'] === 'string' &&
    (value['request_type'] === 'new_tenant' || value['request_type'] === 'join_tenant') &&
    (value['status'] === 'pending' || value['status'] === 'approved' || value['status'] === 'rejected')
  );
}

function isStoredOnboardingState(value: unknown): value is StoredOnboardingState {
  if (
    !isRecord(value) ||
    typeof value['token'] !== 'string' ||
    readOnboardingIdentity(value['identity']) === null
  ) {
    return false;
  }

  return value['request'] === null || isAccessRequestResponse(value['request']);
}

function readStoredOnboardingState(): StoredOnboardingState | null {
  const rawState = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
  if (rawState === null || rawState.trim().length === 0) {
    return null;
  }

  let parsedState: unknown;
  try {
    parsedState = JSON.parse(rawState);
  } catch (error) {
    if (error instanceof SyntaxError) {
      sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
      return null;
    }

    throw error;
  }

  if (!isStoredOnboardingState(parsedState)) {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return null;
  }

  const identity = readOnboardingIdentity(parsedState['identity']);
  if (identity === null) {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return null;
  }

  return {
    identity,
    request: parsedState['request'],
    token: parsedState['token'],
  };
}

function writeStoredOnboardingState(state: StoredOnboardingState | null): void {
  if (state === null) {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
}

function toAuthErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [availableMemberships, setAvailableMemberships] = useState<
    readonly MembershipSummaryResponse[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [hasAuthenticatedSession, setHasAuthenticatedSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingIdentity, setOnboardingIdentity] = useState<OnboardingIdentity | null>(null);
  const [onboardingRequest, setOnboardingRequest] = useState<AccessRequestResponse | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<
    readonly PendingInvitationResponse[]
  >([]);
  const [pendingPlatformInvitations, setPendingPlatformInvitations] = useState<
    readonly PendingPlatformInvitationResponse[]
  >([]);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const [sessionRenewalState, setSessionRenewalState] =
    useState<SessionRenewalDialogState>('action_required');
  const [sessionRenewalError, setSessionRenewalError] = useState<string | null>(null);
  const lastSessionActivityAtRef = useRef(0);
  const lastScrollActivityAtRef = useRef(Number.NEGATIVE_INFINITY);
  const renewalPromiseRef = useRef<Promise<boolean> | null>(null);
  const sessionOperationRef = useRef<Promise<unknown>>(Promise.resolve());
  const userRef = useRef<AuthUser | null>(null);
  const showExpiredDialogRef = useRef(false);
  const lastSuccessfulRenewalAtRef = useRef<number | null>(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    showExpiredDialogRef.current = showExpiredDialog;
  }, [showExpiredDialog]);

  useEffect(() => {
    if (user !== null) {
      lastSessionActivityAtRef.current = Date.now();
      return;
    }

    lastScrollActivityAtRef.current = Number.NEGATIVE_INFINITY;
  }, [user]);

  const runSerializedSessionOperation = useCallback(
    <T,>(operation: () => Promise<T>): Promise<T> => {
      const previousOperation = sessionOperationRef.current.catch(() => undefined);
      const nextOperation = previousOperation.then(operation);
      sessionOperationRef.current = nextOperation.catch(() => undefined);
      return nextOperation;
    },
    [],
  );

  const showSessionContinuation = useCallback((
    state: SessionRenewalDialogState,
    errorMessage: string | null = null,
  ) => {
    setSessionRenewalState(state);
    setSessionRenewalError(errorMessage);
    setShowExpiredDialog(true);
  }, []);

  const clearOnboardingState = useCallback(() => {
    writeStoredOnboardingState(null);
    setOnboardingIdentity(null);
    setOnboardingRequest(null);
  }, []);

  const clearAuthenticatedSessionState = useCallback(() => {
    setHasAuthenticatedSession(false);
    setAvailableMemberships([]);
    setPendingInvitations([]);
    setPendingPlatformInvitations([]);
    setUser(null);
  }, []);

  const applyAuthenticatedSession = useCallback(async (
    session: Awaited<ReturnType<typeof apiClient.getAuthSession>>,
  ): Promise<void> => {
    setAvailableMemberships(session.available_memberships);
    setPendingInvitations(session.pending_invitations);
    setPendingPlatformInvitations(session.pending_platform_invitations);

    if (!session.authenticated || session.user == null) {
      setHasAuthenticatedSession(false);
      setUser(null);
      return;
    }

    const currentUser = await apiClient.getCurrentUser();
    setUser(buildAuthUser(session.user, currentUser));
    setHasAuthenticatedSession(true);
  }, []);

  const storeOnboardingState = useCallback(
    (state: StoredOnboardingState) => {
      writeStoredOnboardingState(state);
      clearAuthenticatedSessionState();
      setOnboardingIdentity(state.identity);
      setOnboardingRequest(state.request);
    },
    [clearAuthenticatedSessionState],
  );

  const hydrateAuthenticatedUser = useCallback(
    async (
      bootstrapSession?: {
        readonly user: SessionUserResponse;
        readonly availableMemberships: readonly MembershipSummaryResponse[];
        readonly pendingInvitations: readonly PendingInvitationResponse[];
        readonly pendingPlatformInvitations: readonly PendingPlatformInvitationResponse[];
      },
    ) => {
      // When the caller has just completed an interactive bootstrap, use the
      // registered session payload directly instead of issuing a second cookie-
      // backed /auth/session request. That follow-up call is what races with
      // the browser's cookie-persistence step on the same tab and produces the
      // stuck "Continue session" state that the "reopen in a new tab"
      // workaround was papering over.
      let sessionUser: SessionUserResponse;
      let availableMemberships: readonly MembershipSummaryResponse[];
      let invitations: readonly PendingInvitationResponse[];
      let platformInvitations: readonly PendingPlatformInvitationResponse[];
      if (bootstrapSession !== undefined) {
        sessionUser = bootstrapSession.user;
        availableMemberships = bootstrapSession.availableMemberships;
        invitations = bootstrapSession.pendingInvitations;
        platformInvitations = bootstrapSession.pendingPlatformInvitations;
      } else {
        const session = await apiClient.getAuthSession();
        if (!session.authenticated || session.user == null) {
          throw new Error(
            'The secure sign-in completed, but the browser session cookie was not established. Check cookie settings and try again.',
          );
        }
        sessionUser = session.user;
        availableMemberships = session.available_memberships;
        invitations = session.pending_invitations;
        platformInvitations = session.pending_platform_invitations;
      }

      const currentUser = await fetchCurrentUserWithCookieRetry();
      clearOnboardingState();
      setHasAuthenticatedSession(true);
      setUser(buildAuthUser(sessionUser, currentUser));
      setAvailableMemberships(availableMemberships);
      setPendingInvitations(invitations);
      setPendingPlatformInvitations(platformInvitations);
      markUserAsVisited();
    },
    [clearOnboardingState],
  );

  const reconcileAuthenticatedSession = useCallback(async (): Promise<boolean> => {
    const session = await apiClient.getAuthSession();
    if (!session.authenticated) {
      clearAuthenticatedSessionState();
      return false;
    }

    clearOnboardingState();
    await applyAuthenticatedSession(session);
    markUserAsVisited();
    setError(null);
    setShowExpiredDialog(false);
    return true;
  }, [applyAuthenticatedSession, clearAuthenticatedSessionState, clearOnboardingState]);

  const applyBootstrapResult = useCallback(
    async (bootstrap: AuthBootstrapResponse, token: string) => {
      if (bootstrap.status === 'registered') {
        await hydrateAuthenticatedUser({
          user: bootstrap.user,
          availableMemberships: bootstrap.available_memberships,
          pendingInvitations: bootstrap.pending_invitations,
          pendingPlatformInvitations: bootstrap.pending_platform_invitations,
        });
        return;
      }

      if (bootstrap.status === 'pending_request') {
        storeOnboardingState({
          identity: bootstrap.identity,
          request: bootstrap.request,
          token,
        });
        markUserAsVisited();
        return;
      }

      storeOnboardingState({
        identity: bootstrap.identity,
        request: null,
        token,
      });
      markUserAsVisited();
    },
    [hydrateAuthenticatedUser, storeOnboardingState],
  );

  const acquireFreshOnboardingToken = useCallback(async (
    fallbackMessage: string,
  ): Promise<string> => {
    try {
      const tokenResult = await acquireIdentityTokenSilent();
      if (tokenResult !== null) {
        return tokenResult.token;
      }
    } catch (error) {
      const message = toAuthErrorMessage(error, fallbackMessage);
      setError(message);
      showSessionContinuation('action_required');
      throw new Error(message, { cause: error });
    }

    const storedOnboardingState = readStoredOnboardingState();
    const storedToken = storedOnboardingState?.token.trim();
    if (
      storedOnboardingState !== null &&
      onboardingIdentity !== null &&
      storedOnboardingState.identity.provider_subject === onboardingIdentity.provider_subject &&
      storedOnboardingState.identity.identity_provider_tenant_id === onboardingIdentity.identity_provider_tenant_id &&
      storedToken !== undefined &&
      storedToken.length > 0
    ) {
      return storedToken;
    }

    setError(fallbackMessage);
    showSessionContinuation('action_required');
    throw new Error(fallbackMessage);
  }, [onboardingIdentity, showSessionContinuation]);

  const renewSessionSilently = useCallback(async (): Promise<boolean> => {
    if (renewalPromiseRef.current !== null) {
      return await renewalPromiseRef.current;
    }

    const renewalPromise = runSerializedSessionOperation(async () => {
      setSessionRenewalState('checking');
      setSessionRenewalError(null);
      try {
        const tokenResult = await acquireIdentityTokenSilent();
        if (tokenResult === null) {
          setSessionRenewalState('action_required');
          publishAuthFailed();
          return false;
        }

        const bootstrap = await apiClient.bootstrapIdentityFromToken(tokenResult.token);
        if (bootstrap.status !== 'registered') {
          setSessionRenewalState('action_required');
          publishAuthFailed();
          return false;
        }

        await hydrateAuthenticatedUser({
          user: bootstrap.user,
          availableMemberships: bootstrap.available_memberships,
          pendingInvitations: bootstrap.pending_invitations,
          pendingPlatformInvitations: bootstrap.pending_platform_invitations,
        });
        setError(null);
        setSessionRenewalError(null);
        setSessionRenewalState('action_required');
        setShowExpiredDialog(false);
        lastSuccessfulRenewalAtRef.current = Date.now();
        publishAuthRenewed();
        return true;
      } catch {
        setSessionRenewalState('action_required');
        publishAuthFailed();
        return false;
      }
    }).finally(() => {
      if (renewalPromiseRef.current === renewalPromise) {
        renewalPromiseRef.current = null;
      }
    });

    renewalPromiseRef.current = renewalPromise;
    return await renewalPromise;
  }, [hydrateAuthenticatedUser, runSerializedSessionOperation]);

  const renewSessionIfRecentlyActive = useCallback((): void => {
    const currentUser = userRef.current;
    if (currentUser === null) {
      return;
    }

    const nowMs = Date.now();
    if (!sessionCanBeProactivelyRenewed(currentUser, nowMs)) {
      return;
    }
    if (nowMs - lastSessionActivityAtRef.current > SESSION_RENEWAL_BUFFER_MS) {
      return;
    }

    void renewSessionSilently().then((renewed) => {
      if (!renewed) {
        showSessionContinuation('action_required');
      }
    });
  }, [renewSessionSilently, showSessionContinuation]);

  useEffect(() => subscribeAuthRecovery(() => {
    const currentUser = userRef.current;
    if (currentUser === null) {
      return false;
    }

    if (getSessionTimeRemainingMs(currentUser, Date.now()) <= 0) {
      showSessionContinuation('action_required');
      return false;
    }

    // Coalesce repeat triggers: if the renewal dialog is already presented,
    // or we just successfully renewed (in-flight 401s often arrive after
    // recovery completes), treat this as already handled to break the
    // "Continue session" flash loop.
    if (showExpiredDialogRef.current) {
      return true;
    }
    const lastRenewalAt = lastSuccessfulRenewalAtRef.current;
    if (lastRenewalAt !== null && Date.now() - lastRenewalAt < SESSION_RENEWAL_COOLDOWN_MS) {
      return true;
    }

    return renewSessionSilently().then((renewed) => {
      if (!renewed) {
        showSessionContinuation('action_required');
      }
      return renewed;
    });
  }), [renewSessionSilently, showSessionContinuation]);

  useEffect(() => subscribeAuthRenewed((event) => {
    if (event.source === 'local') {
      return;
    }

    void runSerializedSessionOperation(reconcileAuthenticatedSession).catch((error: unknown) => {
      setError(toAuthErrorMessage(error, 'The session could not be refreshed from another tab.'));
    });
  }), [reconcileAuthenticatedSession, runSerializedSessionOperation]);

  useEffect(() => subscribeAuthSignedOut((event) => {
    if (event.source === 'local') {
      return;
    }

    renewalPromiseRef.current = null;
    clearAuthenticatedSessionState();
    clearOnboardingState();
    setShowExpiredDialog(false);
    setSessionRenewalError(null);
    setSessionRenewalState('action_required');
    setIsLoading(false);
  }), [clearAuthenticatedSessionState, clearOnboardingState]);

  const synchronizeOnboardingRequest = useCallback(
    async (token: string, identity: OnboardingIdentity): Promise<MyAccessRequestStatusResponse> => {
      const requestStatus = await apiClient.getMyAccessRequestStatus(token);
      if (requestStatus.status === 'pending') {
        storeOnboardingState({
          identity,
          request: requestStatus.request,
          token,
        });
        return requestStatus;
      }

      const bootstrap = await apiClient.bootstrapIdentityFromToken(token);
      await applyBootstrapResult(bootstrap, token);
      if (bootstrap.status === 'pending_request') {
        return {
          request: bootstrap.request,
          status: 'pending',
        };
      }

      return { status: 'none' };
    },
    [applyBootstrapResult, storeOnboardingState],
  );

  useEffect(() => {
    const restoreSessionState = { active: true };
    const restoreWasCancelled = (): boolean => !restoreSessionState.active;

    async function restoreSession(): Promise<void> {
      const storedOnboardingState = readStoredOnboardingState();
      let authenticatedSessionDetected = false;
      setError(null);
      setIsLoading(true);
      await withCleanup(async () => {
        try {
          const session = await apiClient.getAuthSession();
          if (session.authenticated) {
            authenticatedSessionDetected = true;
            if (restoreWasCancelled()) {
              return;
            }

            clearOnboardingState();
            await applyAuthenticatedSession(session);
            if (restoreWasCancelled()) {
              return;
            }

            markUserAsVisited();
            return;
          }

          if (restoreWasCancelled()) {
            return;
          }

          if (storedOnboardingState !== null) {
            setUser(null);
            setOnboardingIdentity(storedOnboardingState.identity);
            setOnboardingRequest(storedOnboardingState.request);
            return;
          }

          clearOnboardingState();
          clearAuthenticatedSessionState();
        } catch (error) {
          if (restoreWasCancelled()) {
            return;
          }

          if (!authenticatedSessionDetected && storedOnboardingState !== null) {
            setUser(null);
            setOnboardingIdentity(storedOnboardingState.identity);
            setOnboardingRequest(storedOnboardingState.request);
            return;
          }

          setError(toAuthErrorMessage(error, 'The session could not be restored. Try signing in again.'));
          clearOnboardingState();
          clearAuthenticatedSessionState();
        }
      }, () => {
        if (!restoreWasCancelled()) {
          setIsLoading(false);
        }
      });
    }

    void restoreSession();

    return () => {
      restoreSessionState.active = false;
    };
  }, [applyAuthenticatedSession, clearAuthenticatedSessionState, clearOnboardingState]);

  const login = useCallback(async (devPersona?: DevPersona): Promise<void> => {
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        const result = devPersona && isDevAuthMode()
          ? await startDevSessionBootstrap(devPersona)
          : await startInteractiveSessionBootstrap();
        await applyBootstrapResult(result.bootstrap, result.token);
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The browser session could not be created. Try signing in again.',
        );

        setError(message);
        clearAuthenticatedSessionState();
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [applyBootstrapResult, clearAuthenticatedSessionState]);

  const retryOnboardingBootstrap = useCallback(async (): Promise<void> => {
    if (onboardingIdentity === null) {
      throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
    }

    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        const token = await acquireFreshOnboardingToken(
          'Confirm your secure sign-in to continue onboarding.',
        );
        const bootstrap = await apiClient.bootstrapIdentityFromToken(token);
        await applyBootstrapResult(bootstrap, token);
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The onboarding session could not be refreshed. Sign in again to continue.',
        );

        setError(message);
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [acquireFreshOnboardingToken, applyBootstrapResult, onboardingIdentity]);

  const refreshOnboardingRequest = useCallback(async (): Promise<MyAccessRequestStatusResponse> => {
    if (onboardingIdentity === null) {
      throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
    }

    setError(null);
    setIsLoading(true);
    return await withCleanup(async () => {
      try {
        const token = await acquireFreshOnboardingToken(
          'Confirm your secure sign-in to refresh your onboarding request.',
        );
        return await synchronizeOnboardingRequest(token, onboardingIdentity);
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The onboarding request status could not be refreshed. Try again.',
        );

        setError(message);
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [acquireFreshOnboardingToken, onboardingIdentity, synchronizeOnboardingRequest]);

  const submitNewTenantRequest = useCallback(
    async (input: SubmitNewTenantAccessRequest): Promise<MyAccessRequestStatusResponse> => {
      if (onboardingIdentity === null) {
        throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
      }

      setError(null);
      setIsLoading(true);
      return await withCleanup(async () => {
        try {
          const token = await acquireFreshOnboardingToken(
            'Confirm your secure sign-in to submit your workspace request.',
          );
          await apiClient.submitNewTenantRequest(token, input);
          return await synchronizeOnboardingRequest(token, onboardingIdentity);
        } catch (error) {
          const message = toAuthErrorMessage(
            error,
            'The workspace request could not be submitted. Try again.',
          );

          setError(message);
          throw new Error(message, { cause: error });
        }
      }, () => {
        setIsLoading(false);
      });
    },
    [acquireFreshOnboardingToken, onboardingIdentity, synchronizeOnboardingRequest],
  );

  const submitJoinTenantRequest = useCallback(
    async (input: SubmitJoinAccessRequest): Promise<MyAccessRequestStatusResponse> => {
      if (onboardingIdentity === null) {
        throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
      }

      setError(null);
      setIsLoading(true);
      return await withCleanup(async () => {
        try {
          const token = await acquireFreshOnboardingToken(
            'Confirm your secure sign-in to submit your workspace join request.',
          );
          await apiClient.submitJoinTenantRequest(token, input);
          return await synchronizeOnboardingRequest(token, onboardingIdentity);
        } catch (error) {
          const message = toAuthErrorMessage(
            error,
            'The workspace join request could not be submitted. Try again.',
          );

          setError(message);
          throw new Error(message, { cause: error });
        }
      }, () => {
        setIsLoading(false);
      });
    },
    [acquireFreshOnboardingToken, onboardingIdentity, synchronizeOnboardingRequest],
  );

  const switchTenant = useCallback(async (tenantId: string): Promise<void> => {
    if (!(await approveSessionChange('switch-workspace'))) throw new SessionChangeDeclinedError();
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        await runSerializedSessionOperation(async () => {
          await apiClient.switchTenant({ tenant_id: tenantId });
          const session = await apiClient.getAuthSession();
          if (session.authenticated) {
            await applyAuthenticatedSession(session);
          } else {
            clearAuthenticatedSessionState();
          }
        });
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The workspace could not be switched. Try again.',
        );
        setError(message);
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [applyAuthenticatedSession, clearAuthenticatedSessionState, runSerializedSessionOperation]);

  const logout = useCallback(async (): Promise<void> => {
    if (!(await approveSessionChange('sign-out'))) return;
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        await apiClient.signOut();
      } catch (error) {
        setError(
          toAuthErrorMessage(
            error,
            'The session could not be ended cleanly. You have been signed out locally.',
          ),
        );
      }
    }, () => {
      clearAuthenticatedSessionState();
      clearOnboardingState();
      setShowExpiredDialog(false);
      setSessionRenewalError(null);
      setSessionRenewalState('action_required');
      setIsLoading(false);
      publishAuthSignedOut();
    });
  }, [clearAuthenticatedSessionState, clearOnboardingState]);

  useEffect(() => {
    if (user === null || !proactiveSessionRenewalEnabled()) {
      return undefined;
    }

    const expiresAtMs = user.expires_at * 1000;
    const timeUntilRenewal = expiresAtMs - Date.now() - SESSION_RENEWAL_BUFFER_MS;
    const delayMs = timeUntilRenewal <= 0
      ? 0
      : Math.max(timeUntilRenewal, SESSION_RENEWAL_MIN_DELAY_MS);
    const timeoutId = window.setTimeout(() => {
      renewSessionIfRecentlyActive();
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [renewSessionIfRecentlyActive, user]);

  useEffect(() => {
    if (user === null || typeof window === 'undefined') {
      return undefined;
    }

    const expiresAtMs = user.expires_at * 1000;
    const delayMs = Math.max(expiresAtMs - Date.now(), 0);
    const timeoutId = window.setTimeout(() => {
      const currentUser = userRef.current;
      if (currentUser === null || renewalPromiseRef.current !== null) {
        return;
      }

      if (getSessionTimeRemainingMs(currentUser, Date.now()) <= 0) {
        showSessionContinuation('action_required');
      }
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showSessionContinuation, user]);

  useEffect(() => {
    if (
      user === null ||
      !proactiveSessionRenewalEnabled() ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    function recordUserActivity(): void {
      lastSessionActivityAtRef.current = Date.now();
      renewSessionIfRecentlyActive();
    }

    function handleScrollActivity(): void {
      const nowMs = Date.now();
      if (nowMs - lastScrollActivityAtRef.current < SCROLL_ACTIVITY_THROTTLE_MS) {
        return;
      }

      lastScrollActivityAtRef.current = nowMs;
      recordUserActivity();
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        recordUserActivity();
      }
    }

    window.addEventListener('focus', recordUserActivity);
    document.addEventListener('pointerdown', recordUserActivity);
    document.addEventListener('keydown', recordUserActivity);
    document.addEventListener('scroll', handleScrollActivity, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', recordUserActivity);
      document.removeEventListener('pointerdown', recordUserActivity);
      document.removeEventListener('keydown', recordUserActivity);
      document.removeEventListener('scroll', handleScrollActivity, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [renewSessionIfRecentlyActive, user]);

  const handleSignInAfterExpiry = useCallback(() => {
    // Guard: if already signing in, do not trigger another interaction.
    if (sessionRenewalState === 'signing_in') {
      return;
    }

    setSessionRenewalState('signing_in');
    setSessionRenewalError(null);

    // In dev mode, re-bootstrap inline using the current user's identity
    // instead of navigating to /login (which would lose page state).
    const bootstrapPromise = isDevAuthMode()
      ? startDevSessionBootstrap({
        email: userRef.current?.email ?? 'dev@local',
        name: userRef.current?.display_name ?? 'Dev User',
      })
      : startInteractiveSessionBootstrap();

    void runSerializedSessionOperation(async () => {
      const { bootstrap, token } = await bootstrapPromise;
      if (bootstrap.status === 'registered') {
        await hydrateAuthenticatedUser({
          user: bootstrap.user,
          availableMemberships: bootstrap.available_memberships,
          pendingInvitations: bootstrap.pending_invitations,
          pendingPlatformInvitations: bootstrap.pending_platform_invitations,
        });
        setError(null);
        setSessionRenewalState('action_required');
        setSessionRenewalError(null);
        setShowExpiredDialog(false);
        lastSuccessfulRenewalAtRef.current = Date.now();
        publishAuthRenewed();
        return;
      }

      await applyBootstrapResult(bootstrap, token);
      setError(null);
      setSessionRenewalState('action_required');
      setSessionRenewalError(null);
      setShowExpiredDialog(false);
    }).catch((error: unknown) => {
      // When MSAL reports that an interaction is already in progress, stay in
      // "signing_in" state instead of flipping to "failed" — opening a second
      // popup would fail immediately and create the flash loop.
      if (
        error instanceof IdentityTokenAcquisitionError
        && error.kind === 'in_progress'
      ) {
        return;
      }

      publishAuthFailed();
      setSessionRenewalState('failed');
      setSessionRenewalError(
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : 'The secure sign-in did not complete. Try again.',
      );
      setShowExpiredDialog(true);
    });
  }, [applyBootstrapResult, hydrateAuthenticatedUser, runSerializedSessionOperation, sessionRenewalState]);

  const acceptTerms = useCallback(async () => {
    const updatedUser = await apiClient.acceptTerms({ terms_version: CURRENT_TERMS_VERSION });
    setUser((currentUser) => {
      if (currentUser === null) {
        return currentUser;
      }

      return {
        ...currentUser,
        accepted_terms_at: updatedUser.accepted_terms_at ?? new Date().toISOString(),
      };
    });
  }, []);

  const hasPermission = useCallback(
    (permission: Permission): boolean =>
      (user?.is_superadmin ?? false) ||
      (user?.permissions.includes(permission) ?? false),
    [user],
  );

  const value = useMemo(
    () => ({
      availableMemberships,
      error,
      isAuthenticated: hasAuthenticatedSession,
      isLoading,
      onboardingIdentity,
      onboardingRequest,
      pendingInvitations,
      pendingPlatformInvitations,
      requiresOnboarding: onboardingIdentity !== null && user === null,
      termsAccepted: hasAcceptedCurrentTerms(user),
      user,
      hasPermission,
      login,
      logout,
      acceptTerms,
      refreshOnboardingRequest,
      retryOnboardingBootstrap,
      submitJoinTenantRequest,
      submitNewTenantRequest,
      switchTenant,
    }),
    [
      acceptTerms,
      availableMemberships,
      error,
      hasAuthenticatedSession,
      hasPermission,
      isLoading,
      login,
      logout,
      onboardingIdentity,
      onboardingRequest,
      pendingInvitations,
      pendingPlatformInvitations,
      refreshOnboardingRequest,
      retryOnboardingBootstrap,
      submitJoinTenantRequest,
      submitNewTenantRequest,
      switchTenant,
      user,
    ],
  );

  return (
    <AuthContext value={value}>
      {children}
      <SessionExpiredDialog
        error={sessionRenewalError}
        open={showExpiredDialog}
        state={sessionRenewalState}
        onSignIn={handleSignInAfterExpiry}
      />
    </AuthContext>
  );
}
